import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase, SUPABASE_AUTH_STORAGE_KEY } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// --- helpers ---
function isRefreshTokenError(err) {
  const msg = String(err?.message || err || '').toLowerCase();
  return (
    msg.includes('refresh token') ||
    msg.includes('invalid refresh') ||
    msg.includes('refresh_token') ||
    msg.includes('token not found') ||
    msg.includes('invalid jwt') ||
    msg.includes('jwt expired')
  );
}

function clearSupabaseAuthStorage() {
  // 1) limpia tu storageKey exacta
  try {
    localStorage.removeItem(SUPABASE_AUTH_STORAGE_KEY);
  } catch (_) {}

  try {
    sessionStorage.removeItem(SUPABASE_AUTH_STORAGE_KEY);
  } catch (_) {}

  // 2) limpieza defensiva (por si Supabase guardó con otro key o preview viejo)
  try {
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith('sb-') && (k.includes('-auth-token') || k.includes('auth'))) {
        localStorage.removeItem(k);
      }
    });
  } catch (_) {}

  try {
    Object.keys(sessionStorage).forEach((k) => {
      if (k.startsWith('sb-') && (k.includes('-auth-token') || k.includes('auth'))) {
        sessionStorage.removeItem(k);
      }
    });
  } catch (_) {}
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // Evitar cargas duplicadas de perfil
  const lastProfileUserIdRef = useRef(null);
  const profileLoadingRef = useRef(false);

  const profileOperations = {
    async load(userId) {
      if (!userId) return;

      // Evita doble carga
      if (lastProfileUserIdRef.current === userId || profileLoadingRef.current) return;

      lastProfileUserIdRef.current = userId;
      profileLoadingRef.current = true;
      setProfileLoading(true);

      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (!error && data) setUserProfile(data);
      } catch (err) {
        console.error('Profile load error:', err);
      } finally {
        setProfileLoading(false);
        profileLoadingRef.current = false;
      }
    },

    clear() {
      lastProfileUserIdRef.current = null;
      profileLoadingRef.current = false;
      setUserProfile(null);
      setProfileLoading(false);
    }
  };

  // ⚠️ Debe ser sincrónico (no await dentro)
  const authStateHandlers = {
    onChange: (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        // fire-and-forget
        profileOperations.load(session.user.id);
      } else {
        profileOperations.clear();
      }
    }
  };

  useEffect(() => {
    let isMounted = true;

    const safeInit = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();

        if (!isMounted) return;

        // Si Supabase reporta error de refresh token → limpiar sin crashear
        if (error && isRefreshTokenError(error)) {
          console.warn('Invalid refresh token on init. Cleaning...', error);
          try {
            await supabase.auth.signOut();
          } catch (_) {}
          clearSupabaseAuthStorage();
          authStateHandlers.onChange('SIGNED_OUT', null);
          return;
        }

        // Si no hay sesión, app sigue normal en modo "no logueado"
        if (!data?.session) {
          authStateHandlers.onChange('NO_SESSION', null);
          return;
        }

        // Sesión válida
        authStateHandlers.onChange('INITIAL_SESSION', data.session);
      } catch (err) {
        if (!isMounted) return;

        if (isRefreshTokenError(err)) {
          console.warn('Refresh token error thrown on init. Cleaning...', err);
          try {
            await supabase.auth.signOut();
          } catch (_) {}
          clearSupabaseAuthStorage();
          authStateHandlers.onChange('SIGNED_OUT', null);
          return;
        }

        console.error('Initial session check failed:', err);
        authStateHandlers.onChange('NO_SESSION', null);
      }
    };

    safeInit();

    // ✅ SOLO 1 listener en toda la app (aquí)
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      // si en algún device llega corrupto en caliente, lo limpiamos
      if (!session && (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN')) {
        // no debería pasar, pero lo dejamos pasar
      }

      authStateHandlers.onChange(event, session);
    });

    return () => {
      isMounted = false;
      listener?.subscription?.unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Auth methods ---
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error && isRefreshTokenError(error)) {
        try {
          await supabase.auth.signOut();
        } catch (_) {}
        clearSupabaseAuthStorage();
      }

      return { data, error };
    } catch (err) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const signUp = async (email, password, metadata) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata }
      });
      return { data, error };
    } catch (err) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      // aunque falle, limpiamos local para evitar bucles
      clearSupabaseAuthStorage();
      authStateHandlers.onChange('SIGNED_OUT', null);
      return { error };
    } catch (err) {
      clearSupabaseAuthStorage();
      authStateHandlers.onChange('SIGNED_OUT', null);
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const getUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      return { data, error };
    } catch (err) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } };
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (!error) setUserProfile(data);
      return { data, error };
    } catch (err) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    getUserProfile,
    isAuthenticated: !!user,
    isAdmin: userProfile?.role === 'admin',
    isAdvisor: userProfile?.role === 'advisor' || userProfile?.role === 'admin'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
